import React, { useState, useEffect } from 'react';
import { apbdService } from '../../../services/apbdService';
import { useAuth } from '../../Admin/context/AuthContext';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaFilePdf,
    FaDownload, FaEye, FaCheckCircle, FaExclamationTriangle
} from 'react-icons/fa';

const ManajemenAPBD = () => {
    const [apbdList, setApbdList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingApbd, setEditingApbd] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [fileUpload, setFileUpload] = useState(null);

    const { user } = useAuth();

    const hasPermission = (permission) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const [formData, setFormData] = useState({
        tahun: new Date().getFullYear(),
        jenis: 'murni',
        judul: '',
        deskripsi: '',
        total_pendapatan: '',
        total_belanja: '',
        total_pembiayaan: '',
        status: 'draft',
    });

    const loadApbd = async () => {
        setLoading(true);
        const result = await apbdService.getApbdList();
        if (result.success) {
            setApbdList(result.data.data || result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadApbd();
    }, []);

    const formatRupiah = (angka) => {
        if (!angka) return '-';
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
        }).format(angka);
    };

    const handleCreate = () => {
        setEditingApbd(null);
        setFormData({
            tahun: new Date().getFullYear(),
            jenis: 'murni',
            judul: '',
            deskripsi: '',
            total_pendapatan: '',
            total_belanja: '',
            total_pembiayaan: '',
            status: 'draft',
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleEdit = (apbd) => {
        setEditingApbd(apbd);
        setFormData({
            tahun: apbd.tahun,
            jenis: apbd.jenis || 'murni',
            judul: apbd.judul,
            deskripsi: apbd.deskripsi || '',
            total_pendapatan: apbd.total_pendapatan || '',
            total_belanja: apbd.total_belanja || '',
            total_pembiayaan: apbd.total_pembiayaan || '',
            status: apbd.status,
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFileUpload(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const submitData = new FormData();
        submitData.append('tahun', formData.tahun);
        submitData.append('jenis', formData.jenis);
        submitData.append('judul', formData.judul);
        submitData.append('deskripsi', formData.deskripsi || '');
        submitData.append('total_pendapatan', formData.total_pendapatan || '0');
        submitData.append('total_belanja', formData.total_belanja || '0');
        submitData.append('total_pembiayaan', formData.total_pembiayaan || '0');
        submitData.append('status', formData.status);

        if (fileUpload) {
            submitData.append('file', fileUpload);
        }

        let result;
        if (editingApbd) {
            result = await apbdService.updateApbd(editingApbd.id, submitData);
        } else {
            result = await apbdService.createApbd(submitData);
        }

        if (result.success) {
            setNotification({ message: result.data?.message || 'Berhasil disimpan', type: 'success' });
            setShowForm(false);
            loadApbd();
        } else {
            setNotification({ message: result.message, type: 'error' });
        }
        setSubmitting(false);
        setTimeout(() => setNotification(null), 3000);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Yakin ingin menghapus data ini?')) {
            const result = await apbdService.deleteApbd(id);
            if (result.success) {
                loadApbd();
            }
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen APBD</h2>
                {hasPermission('apbd.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center">
                        <FaPlus className="mr-2" /> Tambah APBD
                    </button>
                )}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success'
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                    {notification.message}
                </div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr className="text-left text-sm">
                            <th className="p-4">Tahun</th>
                            <th className="p-4">Jenis</th>
                            <th className="p-4">Judul</th>
                            <th className="p-4">Pendapatan</th>
                            <th className="p-4">Belanja</th>
                            <th className="p-4">Pembiayaan</th>
                            <th className="p-4">Status</th>
                            <th className="p-4">File</th>
                            <th className="p-4">Aksi</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apbdList.map((apbd) => (
                            <tr key={apbd.id} className="border-b hover:bg-gray-50">
                                <td className="p-4 font-bold">{apbd.tahun}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${apbd.jenis === 'perubahan'
                                            ? 'bg-purple-100 text-purple-700'
                                            : 'bg-blue-100 text-blue-700'
                                        }`}>
                                        {apbd.jenis === 'perubahan' ? 'APBD Perubahan' : 'APBD'}
                                    </span>
                                </td>
                                <td className="p-4">{apbd.judul}</td>
                                <td className="p-4 text-sm">{formatRupiah(apbd.total_pendapatan)}</td>
                                <td className="p-4 text-sm">{formatRupiah(apbd.total_belanja)}</td>
                                <td className="p-4 text-sm">{formatRupiah(apbd.total_pembiayaan)}</td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded-full text-xs ${apbd.status === 'publish'
                                            ? 'bg-green-100 text-green-700'
                                            : apbd.status === 'arsip'
                                                ? 'bg-gray-100 text-gray-500'
                                                : 'bg-yellow-100 text-yellow-700'
                                        }`}>
                                        {apbd.status}
                                    </span>
                                </td>
                                <td className="p-4">
                                    {apbd.file_name ? (
                                        <span className="text-sm text-gray-500">{apbd.file_name}</span>
                                    ) : '-'}
                                </td>
                                <td className="p-4">
                                    <div className="flex space-x-2">
                                        {hasPermission('apbd.update') && (
                                            <button onClick={() => handleEdit(apbd)} className="p-1 text-blue-500" title="Edit">
                                                <FaEdit />
                                            </button>
                                        )}
                                        {hasPermission('apbd.delete') && (
                                            <button onClick={() => handleDelete(apbd.id)} className="p-1 text-red-500" title="Hapus">
                                                <FaTrash />
                                            </button>
                                        )}
                                        {!hasPermission('apbd.update') && !hasPermission('apbd.delete') && (
                                            <span className="text-xs text-gray-400 italic">-</span>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editingApbd ? 'Edit APBD' : 'Tambah APBD'}
                                </h3>
                                <button onClick={() => setShowForm(false)}>
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm mb-1">Tahun *</label>
                                        <input
                                            type="number"
                                            name="tahun"
                                            value={formData.tahun}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Jenis APBD *</label>
                                        <select
                                            name="jenis"
                                            value={formData.jenis}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                            required
                                        >
                                            <option value="murni">Data APBD</option>
                                            <option value="perubahan">Data APBD Perubahan</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                        >
                                            <option value="draft">Draft</option>
                                            <option value="publish">Publish</option>
                                            <option value="arsip">Arsip</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">Judul *</label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border rounded-lg"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">Deskripsi</label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm mb-1">Pendapatan (Rp)</label>
                                        <input
                                            type="number"
                                            name="total_pendapatan"
                                            value={formData.total_pendapatan}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Belanja (Rp)</label>
                                        <input
                                            type="number"
                                            name="total_belanja"
                                            value={formData.total_belanja}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm mb-1">Pembiayaan (Rp)</label>
                                        <input
                                            type="number"
                                            name="total_pembiayaan"
                                            value={formData.total_pembiayaan}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border rounded-lg"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm mb-1">Upload File (PDF)</label>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        accept=".pdf"
                                        className="w-full px-4 py-2 border rounded-lg"
                                    />
                                    {editingApbd?.file_name && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            File saat ini: {editingApbd.file_name}
                                        </p>
                                    )}
                                </div>

                                <div className="flex justify-end space-x-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2 border rounded-lg"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-[#2E7D32] text-white px-6 py-2 rounded-lg"
                                    >
                                        {submitting ? 'Menyimpan...' : <><FaSave className="mr-2" /> Simpan</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenAPBD;